// ST39 — a tiny, dependency-free SVG line/sparkline. Chart libs are DELIBERATELY
// absent from this project (bundle budget), so this draws a polyline in raw SVG.
//
// Renders one or more series over a shared value/time range. Degrades to an empty
// track when there is no data yet. Pure/presentational — the caller supplies points
// (from the store's SeriesStore).

import type { Point } from '../lib/timeseries';

export interface SparklineSeries {
  points: Point[];
  /** Stroke color; defaults to the accent CSS var. */
  color?: string;
  label?: string;
}

export interface SparklineProps {
  series: SparklineSeries[];
  width?: number;
  height?: number;
  /** Fix the value axis (e.g. 0..100 for a %). Auto-scales when omitted. */
  min?: number;
  max?: number;
  /** Fix the TIME axis to a rolling window of this many ms, anchored at the latest point
   *  (tMin = latestTs - windowMs). Keeps the line scrolling in a STABLE span instead of
   *  rescaling its whole width every time a new point arrives (the sparkline "jitter"). */
  windowMs?: number;
  /** Fill under the (first) line. */
  fill?: boolean;
  className?: string;
  ariaLabel?: string;
}

// Build the SVG polyline points string for one series, mapping data → viewbox coords.
export function buildPath(
  points: Point[],
  vMin: number,
  vMax: number,
  tMin: number,
  tMax: number,
  width: number,
  height: number,
): string {
  if (points.length === 0) return '';
  const span = tMax - tMin || 1;
  const range = vMax - vMin || 1;
  const pad = 1; // keep the stroke off the very edge
  const usableH = height - pad * 2;
  return points
    .map((p) => {
      const x = ((p.ts - tMin) / span) * width;
      const y = pad + (1 - (p.value - vMin) / range) * usableH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

// Shared value + time bounds across all series.
export function bounds(
  series: SparklineSeries[],
  fixedMin?: number,
  fixedMax?: number,
): { vMin: number; vMax: number; tMin: number; tMax: number } {
  let vMin = Infinity;
  let vMax = -Infinity;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const s of series) {
    for (const p of s.points) {
      if (p.value < vMin) vMin = p.value;
      if (p.value > vMax) vMax = p.value;
      if (p.ts < tMin) tMin = p.ts;
      if (p.ts > tMax) tMax = p.ts;
    }
  }
  if (!Number.isFinite(vMin)) {
    vMin = 0;
    vMax = 1;
  }
  if (!Number.isFinite(tMin)) {
    tMin = 0;
    tMax = 1;
  }
  if (fixedMin != null) vMin = fixedMin;
  if (fixedMax != null) vMax = fixedMax;
  // Give a flat line a little vertical room so it renders mid-track.
  if (vMax === vMin) {
    vMax += 1;
    vMin -= 1;
  }
  return { vMin, vMax, tMin, tMax };
}

export function Sparkline({
  series,
  width = 160,
  height = 36,
  min,
  max,
  windowMs,
  fill = false,
  className,
  ariaLabel,
}: SparklineProps) {
  const hasData = series.some((s) => s.points.length > 0);
  const b = bounds(series, min, max);
  const { vMin, vMax } = b;
  let { tMin, tMax } = b;
  // Stable rolling window: pin the span to windowMs anchored at the latest point so the line
  // scrolls instead of the whole width rescaling on every new point (the sparkline jitter).
  if (windowMs && windowMs > 0 && Number.isFinite(tMax)) {
    tMin = tMax - windowMs;
  }

  return (
    <svg
      className={`sparkline ${className ?? ''}`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
    >
      {!hasData && (
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          className="sparkline-empty"
          stroke="currentColor"
          strokeOpacity={0.15}
        />
      )}
      {series.map((s, i) => {
        const path = buildPath(s.points, vMin, vMax, tMin, tMax, width, height);
        if (!path) return null;
        const color = s.color ?? 'var(--accent, #4f8cff)';
        return (
          <g key={i}>
            {fill && i === 0 && (
              <polygon
                points={`0,${height} ${path} ${width},${height}`}
                fill={color}
                fillOpacity={0.12}
                stroke="none"
              />
            )}
            <polyline
              points={path}
              fill="none"
              stroke={color}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </g>
        );
      })}
    </svg>
  );
}
